export type OralExamQuestionPhase = 'reading' | 'pre_read' | 'answering';

/** Seconds until the authoritative deadline (ceil), or null when no deadline. */
export function getOralExamRemainingSeconds(
  questionDeadline?: string | null,
  nowMs: number = Date.now(),
): number | null {
  if (!questionDeadline) return null;
  const deadlineMs = new Date(questionDeadline).getTime();
  return Math.max(0, Math.ceil((deadlineMs - nowMs) / 1000));
}

/** Pre-read countdown N when deadline projects answer_started_at + secondsPerQuestion. */
export function getOralExamPreReadSecondsRemaining(
  questionDeadline: string,
  secondsPerQuestion: number,
  nowMs: number = Date.now(),
): number {
  const remaining = getOralExamRemainingSeconds(questionDeadline, nowMs) ?? 0;
  return Math.max(0, Math.ceil(remaining - secondsPerQuestion));
}

/**
 * Derive question phase from server deadline (no extra API fields).
 * Lecturer-driven: pre_read while (deadline - now) > secondsPerQuestion, then answering.
 * Agent path: reading until answer_window_opened sets a deadline.
 */
export function getOralExamQuestionPhase(
  questionMarkerStartedAt?: string | null,
  questionDeadline?: string | null,
  secondsPerQuestion?: number,
  nowMs: number = Date.now(),
): OralExamQuestionPhase {
  if (!questionDeadline) {
    return 'reading';
  }
  const spq = secondsPerQuestion ?? 0;
  if (spq <= 0) {
    return questionMarkerStartedAt ? 'answering' : 'reading';
  }
  const remaining = getOralExamRemainingSeconds(questionDeadline, nowMs) ?? 0;
  if (remaining > spq) {
    return 'pre_read';
  }
  return 'answering';
}

export function formatOralExamTimer(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
